/**
 * First Message Configuration Panel
 * Configure first message instruction
 * Matches the old admin interface exactly
 */

import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Loading } from '@/components/common';
import { Save, RotateCcw, MessageSquare, AlertTriangle, AlertCircle } from 'lucide-react';
import { useFirstMessageConfig, useUpdateFirstMessageConfig } from '../hooks/useSystemConfig';
import { useI18n } from '@/i18n';

export const FirstMessagePanel: React.FC = () => {
  const { messages } = useI18n();
  const t = messages.systemSettings.firstMessage;
  const { data: config, isLoading, error, refetch } = useFirstMessageConfig();
  const updateConfig = useUpdateFirstMessageConfig();

  const [instruction, setInstruction] = useState('');

  useEffect(() => {
    if (config) {
      setInstruction(config.instruction || '');
    }
  }, [config]);

  const handleSave = async () => {
    // Validate
    if (!instruction || instruction.trim().length < 10) {
      alert(t?.validation?.minLength);
      return;
    }
    if (instruction.length > 5000) {
      alert(t?.validation?.maxLength);
      return;
    }

    try {
      await updateConfig.mutateAsync({
        instruction: instruction.trim(),
        enabled: config?.enabled ?? true,
      });
      alert(t?.saveSuccess);
    } catch (error) {
      alert(`${t?.saveFailed}: ${error}`);
    }
  };

  const handleReloadFromDatabase = () => {
    refetch();
  };

  if (isLoading) {
    return <Loading message={t?.loading} />;
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>{t?.loadFailed}: {error.message}</span>
      </div>
    );
  }

  const hasChanges = instruction !== (config?.instruction || '');

  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              {t?.warningBanner}
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">{t?.title}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {t?.description}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleReloadFromDatabase} disabled={updateConfig.isPending}>
            <RotateCcw className="w-4 h-4 mr-2" />
            {t?.reloadFromDatabase}
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || updateConfig.isPending}>
            <Save className="w-4 h-4 mr-2" />
            {updateConfig.isPending ? t?.saving : t?.saveSystemDefault}
          </Button>
        </div>
      </div>

      {/* Instruction Card */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900">{t?.instructionTitle}</h3>
        </div>

        <Textarea
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          rows={10}
          placeholder={t?.instructionPlaceholder}
          className="font-mono text-sm"
        />

        <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
          <span>
            {t?.instructionLength}: <strong>{instruction.length}</strong>
          </span>
          <span className="text-xs text-gray-400">
            {t?.instructionMinMax}
          </span>
        </div>
      </Card>

      {/* Info Card */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
        <p className="font-medium mb-2">{t?.howItWorksTitle}</p>
        <ul className="space-y-1 list-disc list-inside">
          {t?.howItWorksItems?.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </div>

      {/* Metadata */}
      {config?.version !== undefined && (
        <div className="text-sm text-gray-500">
          <p>
            {t?.version}: <span>{config.version}</span> |
            {t?.lastUpdatedBy}: <span>{config.updated_by || 'N/A'}</span> |
            <span> {config.updated_at ? new Date(config.updated_at).toLocaleString() : 'N/A'}</span>
          </p>
        </div>
      )}
    </div>
  );
};
